import { DOCUMENT_TYPE, getInsuranceTypeName, INSURANCE_TYPE } from './insuranceTypeUtils';
import { isFeatureEnabledDocumentTypeAll } from './featureFlags';

// src/utils/documentTypeUtils.js

/**
 * 文書種別の表示順・選択状態・API送信用の変換をまとめたユーティリティ
 */
export const DOCUMENT_TYPE_ORDER_BY_INSURANCE_TYPE = {
  1: [
    DOCUMENT_TYPE.POLICY_BOOKLET,
    DOCUMENT_TYPE.NEW_CONTRACT,
    DOCUMENT_TYPE.MAINTENANCE,
    DOCUMENT_TYPE.HIKIUKE
  ],
  2: [
    DOCUMENT_TYPE.POLICY_BOOKLET,
    DOCUMENT_TYPE.HANDBOOK
  ]
};

// 保険種別に応じた表示順（未定義は空配列）
/**
 * 保険種別ごとの表示順を取得
 * @param {number|string} insuranceType - 保険種別（1:生保 / 2:損保）
 * @returns {string[]} 文書種別コードの並び順
 */
export const getDocumentTypeOrder = (insuranceType) =>
  DOCUMENT_TYPE_ORDER_BY_INSURANCE_TYPE[insuranceType] || [];

/**
 * 表示順に並べ替えた文書種別一覧を返す
 * @param {Array} documentTypes - 文書種別一覧
 * @param {number|string} insuranceType - 保険種別
 * @returns {Array} 並び替え済み一覧
 */
export const getOrderedDocumentTypes = (documentTypes, insuranceType) => {
  const order = getDocumentTypeOrder(insuranceType);
  // 保険種別で絞り込みつつ、表示順を固定化
  const filteredTypes = (documentTypes || []).filter((type) => {
    if (!insuranceType) {
      return true;
    }
    const typeInsurance = type.insurance_type ?? type.insuranceType ?? null;
    if (typeInsurance === null) {
      return true;
    }
    return Number(typeInsurance) === Number(insuranceType);
  });
  const map = new Map(filteredTypes.map((type) => [type.code, type]));
  // 表示順に沿って並べ替え
  return order.map((code) => map.get(code)).filter(Boolean);
};

/**
 * デフォルトの文書種別コードを決定
 *
 * 1. DocumentTypeAll フラグが有効、かつ引受目安以外に有効な文書種別がある場合 → DOCUMENT_TYPE.ALL を返す
 * 2. 有効な文書種別が存在する場合 → 先頭の有効な文書種別コードを返す
 * 3. 有効な文書種別が存在しない場合（フォールバック）
 *    - DocumentTypeAll フラグが有効なら DOCUMENT_TYPE.ALL を返す
 *    - フラグ無効なら '' を返す
 *
 * @param {Array} documentTypes - 文書種別一覧（バックエンドから取得したもの。ALL は含まない）
 * @param {number|string} insuranceType - 保険種別
 * @returns {string} 文書種別コード
 */
export const getDefaultDocumentTypeCode = (documentTypes, insuranceType) => {
  // 保険種別でフィルタした上で判定することで、他の保険種別の有効状態を誤って含めない
  const ordered = getOrderedDocumentTypes(documentTypes, insuranceType);
  // 「すべて」フラグが有効、かつ当該保険種別で引受目安以外に有効な文書種別がある場合のみ ALL をデフォルトとする
  if (isFeatureEnabledDocumentTypeAll() && hasEnabledNonDocumentTypeAll(ordered)) {
    return DOCUMENT_TYPE.ALL;
  }
  // 有効な文書種別が存在する場合はその先頭を返す。
  // 存在しない場合（フォールバック）は DocumentTypeAll フラグが有効なら ALL、無効なら '' を返す
  const enabled = ordered.find((type) => type.is_enabled);
  return enabled?.code ?? (isFeatureEnabledDocumentTypeAll() ? DOCUMENT_TYPE.ALL : '');
};

/**
 * UIの選択状態に使うキーを生成
 * @param {Object} type - 文書種別
 * @returns {string} `${insuranceType}:${code}` 形式のキー
 */
export const getDocumentTypeSelectionKey = (type) => {
  const insuranceType = type?.insurance_type ?? type?.insuranceType ?? '';
  const code = type?.code ?? '';
  // 文書種別の選択状態を保険種別込みで一意化
  return `${insuranceType}:${code}`;
};

/**
 * 文書種別ラベル表示用の固定順
 * @returns {Array<{code:string, insuranceType:number}>}
 */
export const getDocumentTypeSelectionOrder = () => ([
  { code: DOCUMENT_TYPE.POLICY_BOOKLET, insuranceType: INSURANCE_TYPE.LIFE },
  { code: DOCUMENT_TYPE.NEW_CONTRACT, insuranceType: INSURANCE_TYPE.LIFE },
  { code: DOCUMENT_TYPE.MAINTENANCE, insuranceType: INSURANCE_TYPE.LIFE },
  { code: DOCUMENT_TYPE.HIKIUKE, insuranceType: INSURANCE_TYPE.LIFE },
  { code: DOCUMENT_TYPE.POLICY_BOOKLET, insuranceType: INSURANCE_TYPE.SONPO },
  { code: DOCUMENT_TYPE.HANDBOOK, insuranceType: INSURANCE_TYPE.SONPO }
]);

/**
 * 選択済み文書種別を表示用テキストに変換
 * @param {string[]} documentTypeIds - 選択キー配列
 * @param {Array} documentTypes - 文書種別一覧
 * @returns {string} 表示用テキスト
 */
export const getDocumentTypeText = (documentTypeIds = [], documentTypes = []) => {
  const selectedKeys = Array.isArray(documentTypeIds) ? documentTypeIds : [];
  if (selectedKeys.length === 0 || documentTypes.length === 0) {
    return 'なし';
  }
  const orderedSelection = getDocumentTypeSelectionOrder();
  const selectedSet = new Set(selectedKeys);
  const labels = orderedSelection
    .filter((item) => selectedSet.has(`${item.insuranceType}:${item.code}`))
    .map((item) => {
      const match = documentTypes.find((type) => (
        type.code === item.code &&
        Number(type.insurance_type ?? type.insuranceType) === item.insuranceType
      )) || documentTypes.find((type) => type.code === item.code);
      const name = match?.name || match?.code || item.code;
      return `${name}（${getInsuranceTypeName(item.insuranceType)}）`;
    });
  return labels.length > 0 ? labels.join('、') : 'なし';
};

/**
 * APIレスポンスを文書種別一覧に正規化
 * @param {Object|Array} response - APIレスポンス
 * @returns {Array} 正規化済み文書種別
 */
export const normalizeDocumentTypes = (response) => {
  // APIレスポンスの形式差を吸収して一覧を正規化
  const rawList = Array.isArray(response)
    ? response
    : response?.results || response?.document_types || [];
  return rawList
    .map((item) => {
      const nested = item?.document_type ?? null;
      const code = item?.code ?? item?.document_type_code ?? item?.document_type ?? item?.documentType ?? nested?.code ?? null;
      const insuranceType =
        item?.insurance_type ?? item?.insuranceType ?? item?.insurancetype ??
        nested?.insurance_type ?? nested?.insuranceType ?? null;
      const name =
        item?.name ?? item?.document_type_name ?? nested?.name ?? item?.label ?? code ?? null;
      const id = item?.id ?? item?.document_type_id ?? nested?.id ?? null;
      const isEnabled = item?.is_enabled ?? item?.isEnabled ?? nested?.is_enabled ?? null;
      return {
        id,
        code,
        name,
        insurance_type: insuranceType,
        is_enabled: isEnabled
      };
    })
    .filter((item) => item.code);
};

/**
 * 文書種別をUI選択キーに変換
 * @param {Array} selectedTypes - APIの文書種別配列
 * @param {Array} types - 文書種別マスタ
 * @returns {string[]} 選択キー配列
 */
export const buildDocumentTypeSelectionKeys = (selectedTypes, types = []) => {
  if (!Array.isArray(selectedTypes)) {
    return [];
  }
  // API由来の形式差を吸収して選択キーへ変換
  const keys = selectedTypes
    .map((entry) => {
      const insuranceType = entry?.insurance_type ?? entry?.insuranceType ?? null;
      const code = entry?.document_type_code ?? entry?.code ?? entry?.document_type ?? entry?.documentType ?? null;
      if (insuranceType && code) {
        // 保険種別+コードが揃う場合はそのままキー化
        return `${insuranceType}:${code}`;
      }
      const entryId = entry?.document_type_id ?? entry?.id ?? null;
      if (entryId !== null && entryId !== undefined) {
        // ID一致で補完（保険種別欠落へのフォールバック）
        const matchById = types.find((type) => type.id === entryId);
        if (matchById) {
          return getDocumentTypeSelectionKey(matchById);
        }
      }
      if (code) {
        // コード一致で補完（保険種別欠落へのフォールバック）
        const matchByCode = types.find((type) => type.code === code);
        if (matchByCode) {
          return getDocumentTypeSelectionKey(matchByCode);
        }
      }
      return null;
    })
    .filter(Boolean);
  // 重複排除
  return Array.from(new Set(keys));
};

/**
 * UIの選択キーからAPI送信用データを生成
 * @param {string[]} selectedKeys - 選択キー配列
 * @param {Array} types - 文書種別マスタ
 * @returns {Array} API送信用文書種別配列
 */
export const buildDocumentTypesForApi = (selectedKeys, types = []) => {
  if (!Array.isArray(selectedKeys)) {
    return [];
  }
  // UIの選択キーをAPI送信用の配列へ変換
  const selections = selectedKeys
    .map((value) => {
      if (typeof value !== 'string') {
        return null;
      }
      if (!value.includes(':')) {
        return null;
      }
      const [insuranceTypeRaw, code] = value.split(':');
      if (!code) {
        return null;
      }
      const insuranceType = insuranceTypeRaw === '' ? Number.NaN : Number(insuranceTypeRaw);
      const match = types.find((type) => {
        if (type.code !== code) {
          return false;
        }
        if (Number.isNaN(insuranceType)) {
          // 保険種別が欠ける場合はコード一致のみ許容
          return true;
        }
        return Number(type.insurance_type ?? type.insuranceType) === insuranceType;
      });
      if (!match) {
        return null;
      }
      // APIが期待する形式に整形
      return {
        id: match.id ?? null,
        document_type: match.code ?? null,
        insurance_type: match.insurance_type ?? match.insuranceType ?? null
      };
    })
    .filter(Boolean);
  return selections;
};

/**
 * 「すべて」以外に有効な文書種別が1つ以上あるかを判定
 *
 * 判定対象: ALL でも HIKIUKE でもない文書種別（「すべて(引受目安以外)」の有効判定に使用）
 * @param {Array} documentTypes - 文書種別一覧
 * @returns {boolean}
 */
export const hasEnabledNonDocumentTypeAll = (documentTypes) => {
  return (documentTypes || []).some(
    type => type.code !== DOCUMENT_TYPE.ALL &&
            type.code !== DOCUMENT_TYPE.HIKIUKE &&
            type.is_enabled
  );
};

/**
 * 「すべて」選択時に表示する保険会社一覧を取得する
 *
 * 判定ルール:
 *   - しおり・約款にアクセス可能な場合 → 全保険会社
 *   - アクセス不可（生保）             → 新契約関連 or 保全・収納・支払関連 にアクセス可能な会社
 *   - アクセス不可（損保）             → ハンドブック にアクセス可能な会社
 *
 * @param {Object} params
 * @param {Array}  params.documentTypesAccess       - アクセス可能な文書種別一覧（is_enabled を含む）
 * @param {Array}  params.agencyInsuranceCompanies  - approved_document_types 付き代理店保険会社一覧
 * @param {Array}  params.allInsuranceCompanies     - 全保険会社一覧
 * @param {number} params.insuranceType             - 保険種別（INSURANCE_TYPE.LIFE / SONPO）
 * @returns {Array} 表示対象の保険会社一覧
 */
export const getCompaniesForDocumentTypeAll = ({
  documentTypesAccess,
  agencyInsuranceCompanies,
  allInsuranceCompanies,
  insuranceType
}) => {
  // しおり・約款へのアクセス権を確認（保険種別も一致させる）
  const canAccessPolicyBooklet = (documentTypesAccess || []).some(
    type => type.code === DOCUMENT_TYPE.POLICY_BOOKLET &&
            type.is_enabled &&
            Number(type.insurance_type) === insuranceType
  );

  if (canAccessPolicyBooklet) {
    return allInsuranceCompanies;
  }

  // しおり・約款にアクセス不可の場合、保険種別に応じた代替文書種別で絞り込む
  const targetCodes = insuranceType === INSURANCE_TYPE.LIFE
    ? [DOCUMENT_TYPE.NEW_CONTRACT, DOCUMENT_TYPE.MAINTENANCE]
    : [DOCUMENT_TYPE.HANDBOOK];

  return (agencyInsuranceCompanies || [])
    .filter(company =>
      Array.isArray(company.approved_document_types) &&
      targetCodes.some(code => company.approved_document_types.includes(code))
    )
    .map(company => ({
      name: company.name,
      nameKana: company.nameKana,
      indexName: company.indexName,
      insuranceType: company.insuranceType
    }));
};


/**
 * 日付範囲選択が適用される文書種別かを判定
 * @param {string} documentTypeCode - 文書種別コード
 * @returns {boolean} 日付範囲が適用される場合true
 */
export const isDateRangeApplicable = (documentTypeCode) => {
  return documentTypeCode === DOCUMENT_TYPE.POLICY_BOOKLET || 
         documentTypeCode === DOCUMENT_TYPE.ALL;
};
